import React from 'react';

interface MediaGalleryBulkSelectionToolbarProps {
    selectedCount: number;
    canUpdateMetadata: boolean;
    canSetPanoColorType: boolean;
    canDelete: boolean;
    onClearSelection: () => void;
    onUpdateMetadata: () => void | Promise<void>;
    onMarkColor: () => void | Promise<void>;
    onMarkBlackAndWhite: () => void | Promise<void>;
    onDeleteMedia: () => void | Promise<void>;
    className?: string;
}

const MediaGalleryBulkSelectionToolbar: React.FC<MediaGalleryBulkSelectionToolbarProps> = ({
    selectedCount,
    canUpdateMetadata,
    canSetPanoColorType,
    canDelete,
    onClearSelection,
    onUpdateMetadata,
    onMarkColor,
    onMarkBlackAndWhite,
    onDeleteMedia,
    className,
}) => {
    const [isOverflowMenuOpen, setIsOverflowMenuOpen] = React.useState(false);
    const overflowMenuRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (selectedCount === 0 && isOverflowMenuOpen) {
            setIsOverflowMenuOpen(false);
        }
    }, [isOverflowMenuOpen, selectedCount]);

    React.useEffect(() => {
        if (!isOverflowMenuOpen) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target as Node)) {
                setIsOverflowMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [isOverflowMenuOpen]);

    const handleUpdateMetadataClick = React.useCallback(() => {
        setIsOverflowMenuOpen(false);
        onUpdateMetadata();
    }, [onUpdateMetadata]);

    const handleDeleteClick = React.useCallback(() => {
        setIsOverflowMenuOpen(false);
        onDeleteMedia();
    }, [onDeleteMedia]);

    const handleMarkColorClick = React.useCallback(() => {
        setIsOverflowMenuOpen(false);
        onMarkColor();
    }, [onMarkColor]);

    const handleMarkBlackAndWhiteClick = React.useCallback(() => {
        setIsOverflowMenuOpen(false);
        onMarkBlackAndWhite();
    }, [onMarkBlackAndWhite]);

    if (selectedCount === 0) {
        return null;
    }

    const hasActions = canUpdateMetadata || canSetPanoColorType || canDelete;
    const rootClassName = className
        ? `knowledge-library-selection-toolbar media-gallery-selection-toolbar ${className}`
        : 'knowledge-library-selection-toolbar media-gallery-selection-toolbar';

    return (
        <div className={rootClassName}>
            <button
                type="button"
                className="knowledge-library-selection-clear-button"
                onClick={onClearSelection}
                title={InovxLoader.t('facility:knowledgeBook.clearSelection')}
                aria-label={InovxLoader.t('facility:knowledgeBook.clearSelection')}
            >
                <span className="fa fa-check-square-o" aria-hidden="true"></span>
            </button>
            <span className="knowledge-library-selection-count">
                {InovxLoader.t('facility:knowledgeBook.selectedCount', { count: selectedCount })}
            </span>
            <div className="knowledge-library-selection-toolbar-spacer" />
            {hasActions && (
                <div className="knowledge-library-overflow-menu" ref={overflowMenuRef}>
                    <button
                        type="button"
                        className="knowledge-library-overflow-trigger"
                        title={InovxLoader.t('facility:mediaGallery.selectionActions')}
                        aria-label={InovxLoader.t('facility:mediaGallery.selectionActions')}
                        aria-haspopup="true"
                        aria-expanded={isOverflowMenuOpen}
                        onClick={() => setIsOverflowMenuOpen(open => !open)}
                    >
                        <span className="fa fa-bars" aria-hidden="true"></span>
                    </button>
                    {isOverflowMenuOpen && (
                        <div className="knowledge-library-overflow-list" role="menu">
                            {canUpdateMetadata && (
                                <button
                                    type="button"
                                    className="knowledge-library-overflow-item"
                                    role="menuitem"
                                    onClick={handleUpdateMetadataClick}
                                >
                                    <span className="fa fa-pencil" aria-hidden="true"></span>
                                    {InovxLoader.t('facility:mediaGallery.updateMediaFileMetaData')}
                                </button>
                            )}
                            {canSetPanoColorType && (
                                <>
                                    {canUpdateMetadata && <div className="knowledge-library-overflow-separator" />}
                                    <button
                                        type="button"
                                        className="knowledge-library-overflow-item"
                                        role="menuitem"
                                        onClick={handleMarkColorClick}
                                    >
                                        <span className="fa fa-circle" aria-hidden="true"></span>
                                        {InovxLoader.t('facility:menu.colorPanos')}
                                    </button>
                                    <button
                                        type="button"
                                        className="knowledge-library-overflow-item"
                                        role="menuitem"
                                        onClick={handleMarkBlackAndWhiteClick}
                                    >
                                        <span className="fa fa-circle-o" aria-hidden="true"></span>
                                        {InovxLoader.t('facility:menu.blackAndWhitePanos')}
                                    </button>
                                </>
                            )}
                            {canDelete && (
                                <>
                                    {(canUpdateMetadata || canSetPanoColorType) && <div className="knowledge-library-overflow-separator" />}
                                    <button
                                        type="button"
                                        className="knowledge-library-overflow-item"
                                        role="menuitem"
                                        onClick={handleDeleteClick}
                                    >
                                        <span className="fa fa-trash-o" aria-hidden="true"></span>
                                        {InovxLoader.t('facility:mediaGallery.deleteMediaFile')}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MediaGalleryBulkSelectionToolbar;
